import React, { useState, forwardRef } from 'react';
import { Icon, TooltipHost, mergeStyleSets } from '@fluentui/react';
import { DotsSixVertical, X as XIcon, PencilSimple } from '@phosphor-icons/react';
import ChartPanel from '../chat/ChartPanel';
import ErrorBoundary from '../ErrorBoundary';
import type { DashboardWidget, WidgetType } from '../../types/dashboard.types';
import type { ChartPanelChartType } from '../chat/ChartPanel';

/** Format large numbers compactly: 70598148 → "$70.6M", 4500 → "$4.5K" */
function formatCompact(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const str = String(value);
  const currencyMatch = str.match(/^([£€$¥])/);
  const prefix = currencyMatch ? currencyMatch[1] : '';
  const num = Number(str.replace(/[^0-9.\-eE]/g, ''));
  if (isNaN(num)) return str;
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}${prefix}${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000)     return `${sign}${prefix}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)         return `${sign}${prefix}${(abs / 1_000).toFixed(1)}K`;
  if (Number.isInteger(num)) return `${prefix}${num.toLocaleString()}`;
  return `${prefix}${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

/** Is a table cell value numeric? */
function isNumericValue(v: unknown): boolean {
  if (v === null || v === undefined || v === '') return false;
  return !isNaN(Number(String(v).replace(/[$,\s%]/g, '')));
}

interface Props {
  widget: DashboardWidget;
  onRemove: (id: string) => void;
  onResize: (id: string, size: 'small' | 'medium' | 'large') => void;
  onChangeChartType: (id: string, chartType: ChartPanelChartType) => void;
  onChangeWidgetType?: (id: string, newType: WidgetType) => void;
  onUpdateLegendLabels?: (id: string, legendLabels: Record<string, string>) => void;
  readOnly?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

// Only 3 chart types in the visible switcher — covers 95% of use cases
const CHART_SWITCHER: { key: ChartPanelChartType; label: string }[] = [
  { key: 'bar',   label: 'Bar'   },
  { key: 'line',  label: 'Line'  },
  { key: 'donut', label: 'Donut' },
];

const WIDGET_TYPE_OPTIONS: { key: WidgetType; icon: string; label: string }[] = [
  { key: 'kpi',    icon: 'SpeedHigh',    label: 'KPI'    },
  { key: 'number', icon: 'NumberSymbol', label: 'Number' },
  { key: 'chart',  icon: 'BarChart4',    label: 'Chart'  },
  { key: 'table',  icon: 'Table',        label: 'Table'  },
];

const SIZE_LABELS: Record<string, string> = { small: 'S', medium: 'M', large: 'L' };

const TREND: Record<string, { fg: string; bg: string; arrow: string }> = {
  up:   { fg: '#059669', bg: '#ECFDF5', arrow: '↑' },
  down: { fg: '#DC2626', bg: '#FFF1F1', arrow: '↓' },
  flat: { fg: '#6B7280', bg: '#F3F4F6', arrow: '→' },
};

function accent(widget: DashboardWidget): string {
  return widget.colorScheme?.[0] ?? '#2e873d';
}

function humanizeColumn(col: string): string {
  return col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Keyframes injected once ────────────────────────────────────────────────
const KEYFRAMES = `
  @keyframes wFadeUp {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const s = mergeStyleSets({
  // ── Base card ──────────────────────────────────────────────────────────────
  card: {
    background: '#FFFFFF',
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.07)',
    // Two-layer shadow: a tight contact shadow + a larger ambient glow
    boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)',
    overflow: 'visible',
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    animation: 'wFadeUp 0.3s cubic-bezier(0.16,1,0.3,1)',
    position: 'relative' as const,
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    selectors: {
      // Lift + deepen shadow on hover — cards feel physical
      ':hover': {
        boxShadow: '0 4px 20px rgba(0,0,0,0.11), 0 1px 4px rgba(0,0,0,0.06)',
        transform: 'translateY(-1px)',
      },
      ':hover .wc': { opacity: 1 },
    },
  },

  // ── Hover-reveal controls ──────────────────────────────────────────────────
  ctrlWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    opacity: 0,
    transition: 'opacity 0.15s ease',
    flexShrink: 0,
  },
  ctrlBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 4,
    padding: '3px 6px',
    fontSize: 10,
    fontWeight: 700,
    color: '#9CA3AF',
    lineHeight: '1',
    transition: 'all 0.1s ease',
    selectors: { ':hover': { background: '#F3F4F6', color: '#374151' } },
  },
  ctrlBtnOn: { background: '#D1FAE5', color: '#065F46' },
  ctrlRemove: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 4,
    padding: '3px 5px',
    color: '#D1D5DB',
    lineHeight: '1',
    transition: 'all 0.1s ease',
    selectors: { ':hover': { background: '#FEE2E2', color: '#DC2626' } },
  },
  ctrlSep: {
    width: 1,
    height: 12,
    background: '#E5E7EB',
    margin: '0 3px',
    flexShrink: 0,
  },
  drag: {
    cursor: 'grab',
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 3px',
    color: '#D1D5DB',
    flexShrink: 0,
    transition: 'color 0.1s',
    selectors: {
      ':hover': { color: '#9CA3AF' },
      ':active': { cursor: 'grabbing' },
    },
  },

  // ── Chart cards ────────────────────────────────────────────────────────────
  chartHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '13px 16px 10px',
    borderBottom: '1px solid #F0F1F3',
    flexShrink: 0,
    gap: 10,
  },
  chartTitle: {
    fontSize: 12.5,
    fontWeight: 700,
    color: '#0D1117',
    letterSpacing: '-0.01em',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    flex: 1,
    minWidth: 0,
  },
  chartBody: {
    padding: '8px 12px 10px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: 0,
  },

  // ── Table ──────────────────────────────────────────────────────────────────
  tblHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px 8px',
    borderBottom: '1px solid #F0F1F3',
    flexShrink: 0,
  },
  tblTitle: {
    fontSize: 12.5,
    fontWeight: 700,
    color: '#0D1117',
    letterSpacing: '-0.01em',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  tblScroll: { overflowY: 'auto' as const, flex: 1 },
  tbl: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 11.5 },
  th: {
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
    background: '#F8F9FA',
    padding: '7px 12px',
    textAlign: 'left' as const,
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    color: '#9CA3AF',
    borderBottom: '2px solid #E9EAEC',
    whiteSpace: 'nowrap' as const,
  },
  thNum: { textAlign: 'right' as const },
  td: {
    padding: '7px 12px',
    borderBottom: '1px solid #F3F4F6',
    color: '#374151',
    verticalAlign: 'middle' as const,
    selectors: {
      'tr:hover &': { background: '#F9FAFB' },
      'tr:last-child &': { borderBottom: 'none' },
    },
  },
  tdNum: {
    textAlign: 'right' as const,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    color: '#0D1117',
    letterSpacing: '-0.01em',
  },
  tdEven: { background: '#FCFCFD' },

  // ── Legend editor ──────────────────────────────────────────────────────────
  legendWrap: { padding: '8px 16px 12px', borderTop: '1px solid #F0F0F0', background: '#FAFAFA' },
  legendRow:  { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  legendInput: {
    flex: 1, fontSize: 12, padding: '4px 8px',
    border: '1px solid #E1E1E1', borderRadius: 4,
    outline: 'none', color: '#0D1117',
    selectors: { ':focus': { borderColor: '#2e873d' } },
  } as any,
  legendDot:  { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  legendSave: {
    padding: '4px 12px', fontSize: 11, fontWeight: 600,
    border: 'none', borderRadius: 4,
    background: '#2e873d', color: '#fff', cursor: 'pointer', marginTop: 4,
  },
});

// ── Shared controls component ──────────────────────────────────────────────
function Controls({
  widget, onRemove, onResize, onChangeWidgetType,
}: {
  widget: DashboardWidget;
  onRemove: (id: string) => void;
  onResize: (id: string, size: 'small' | 'medium' | 'large') => void;
  onChangeWidgetType?: (id: string, newType: WidgetType) => void;
}) {
  return (
    <div className={`${s.ctrlWrap} wc`}>
      {onChangeWidgetType && (
        <>
          {WIDGET_TYPE_OPTIONS.map((o) => (
            <TooltipHost key={o.key} content={o.label}>
              <button
                className={`${s.ctrlBtn} ${widget.type === o.key ? s.ctrlBtnOn : ''}`}
                onClick={() => onChangeWidgetType(widget.id, o.key)}
              >
                <Icon iconName={o.icon} style={{ fontSize: 10 }} />
              </button>
            </TooltipHost>
          ))}
          <div className={s.ctrlSep} />
        </>
      )}
      {(['small', 'medium', 'large'] as const).map((sz) => (
        <TooltipHost key={sz} content={`Size: ${sz}`}>
          <button
            className={`${s.ctrlBtn} ${widget.size === sz ? s.ctrlBtnOn : ''}`}
            onClick={() => onResize(widget.id, sz)}
          >
            {SIZE_LABELS[sz]}
          </button>
        </TooltipHost>
      ))}
      <button className={s.ctrlRemove} onClick={() => onRemove(widget.id)} title="Remove">
        <XIcon size={10} weight="bold" />
      </button>
    </div>
  );
}

const DashboardWidgetCard = forwardRef<HTMLDivElement, Props>(function DashboardWidgetCard(
  { widget, onRemove, onResize, onChangeChartType, onChangeWidgetType, onUpdateLegendLabels, readOnly, className, style, children },
  ref,
) {
  const [editingLegend, setEditingLegend] = useState(false);
  const [legendDraft, setLegendDraft] = useState<Record<string, string>>({});
  const ac = accent(widget);
  const currentChartType: ChartPanelChartType = widget.chartType || 'bar';

  // ── KPI ───────────────────────────────────────────────────────────────────
  if (widget.type === 'kpi') {
    const trend = TREND[widget.kpiTrend || 'flat'];
    // Derive a very subtle background wash from the accent colour
    const wash = `${ac}0d`; // ~5% opacity tint
    return (
      <div
        ref={ref}
        className={`${s.card} ${className || ''}`}
        style={{
          ...style,
          borderLeft: `5px solid ${ac}`,
          background: `linear-gradient(135deg, ${wash} 0%, #FFFFFF 55%)`,
        }}
        data-widget-id={widget.id}
      >
        <style>{KEYFRAMES}</style>
        <div style={{ padding: '14px 18px 16px', display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: 0 }}>
          {/* Top row: drag + controls */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, minHeight: 18 }}>
            {!readOnly
              ? <span className={`${s.drag} widget-drag-handle`}><DotsSixVertical size={12} weight="regular" /></span>
              : <span />
            }
            {!readOnly && <Controls widget={widget} onRemove={onRemove} onResize={onResize} onChangeWidgetType={onChangeWidgetType} />}
          </div>
          {/* Label — tiny, uppercase, accent-coloured */}
          <div style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '0.13em',
            textTransform: 'uppercase', color: ac, opacity: 0.75,
            marginBottom: 6, lineHeight: 1,
          }}>
            {widget.kpiLabel || widget.title}
          </div>
          {/* Value — the hero element */}
          <TooltipHost content={
            typeof widget.kpiValue === 'number'
              ? widget.kpiValue.toLocaleString(undefined, { maximumFractionDigits: 2 })
              : String(widget.kpiValue ?? '')
          }>
            <div style={{
              fontSize: 38,
              fontWeight: 900,
              color: '#0D1117',
              letterSpacing: '-0.035em',
              lineHeight: 1.05,
              fontVariantNumeric: 'tabular-nums',
              wordBreak: 'break-all',
            }}>
              {formatCompact(widget.kpiValue)}
            </div>
          </TooltipHost>
          {/* Trend pill */}
          {widget.kpiTrend && (
            <div style={{
              marginTop: 10,
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 10, fontWeight: 800,
              padding: '3px 8px', borderRadius: 4,
              background: trend.bg, color: trend.fg,
              width: 'fit-content', letterSpacing: '0.02em',
            }}>
              <span style={{ fontSize: 11 }}>{trend.arrow}</span>
              <span>{widget.kpiLabel || widget.title}</span>
            </div>
          )}
        </div>
        {children}
      </div>
    );
  }

  // ── Number ────────────────────────────────────────────────────────────────
  if (widget.type === 'number') {
    return (
      <div
        ref={ref}
        className={`${s.card} ${className || ''}`}
        style={{ ...style, borderTop: `3px solid ${ac}` }}
        data-widget-id={widget.id}
      >
        <style>{KEYFRAMES}</style>
        {/* Controls float top-right on hover */}
        {!readOnly && (
          <div className="wc" style={{
            position: 'absolute', top: 10, right: 10, zIndex: 2,
            display: 'flex', alignItems: 'center', gap: 4,
            opacity: 0, transition: 'opacity 0.15s',
          }}>
            <span className={`${s.drag} widget-drag-handle`}><DotsSixVertical size={12} /></span>
            <Controls widget={widget} onRemove={onRemove} onResize={onResize} onChangeWidgetType={onChangeWidgetType} />
          </div>
        )}
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          textAlign: 'center', padding: '16px 20px 20px',
        }}>
          <TooltipHost content={
            typeof widget.kpiValue === 'number'
              ? widget.kpiValue.toLocaleString(undefined, { maximumFractionDigits: 2 })
              : String(widget.kpiValue ?? '')
          }>
            <div style={{
              fontSize: 48, fontWeight: 900, color: ac,
              letterSpacing: '-0.04em', lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {formatCompact(widget.kpiValue)}
            </div>
          </TooltipHost>
          <div style={{
            marginTop: 10, fontSize: 9.5, fontWeight: 800,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: '#9CA3AF',
          }}>
            {widget.title}
          </div>
        </div>
        {children}
      </div>
    );
  }

  // ── Table ─────────────────────────────────────────────────────────────────
  if (widget.type === 'table') {
    const fields = widget.chartData?.fields || [];
    const rows   = widget.chartData?.rows   || [];
    // Detect numeric columns from first few rows
    const numericCols = new Set(
      fields.filter((f) =>
        (rows.slice(0, 5)).every((r) => r[f.name] == null || r[f.name] === '' || isNumericValue(r[f.name]))
        && rows.some((r) => r[f.name] != null && r[f.name] !== '')
      ).map((f) => f.name)
    );
    return (
      <div
        ref={ref}
        className={`${s.card} ${className || ''}`}
        style={style}
        data-widget-id={widget.id}
      >
        <style>{KEYFRAMES}</style>
        <div className={s.tblHead}>
          {!readOnly && <span className={`${s.drag} widget-drag-handle`}><DotsSixVertical size={12} /></span>}
          <span className={s.tblTitle}>{widget.title}</span>
          {!readOnly && <Controls widget={widget} onRemove={onRemove} onResize={onResize} onChangeWidgetType={onChangeWidgetType} />}
        </div>
        <div className={s.tblScroll}>
          {rows.length > 0 ? (
            <table className={s.tbl}>
              <thead>
                <tr>
                  {fields.map((f) => (
                    <th key={f.name} className={`${s.th} ${numericCols.has(f.name) ? s.thNum : ''}`}>
                      {humanizeColumn(f.name)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    {fields.map((f) => {
                      const isNum = numericCols.has(f.name);
                      return (
                        <td
                          key={f.name}
                          className={`${s.td} ${isNum ? s.tdNum : ''} ${i % 2 === 1 ? s.tdEven : ''}`}
                        >
                          {row[f.name] != null ? String(row[f.name]) : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 12 }}>
              No data loaded
            </div>
          )}
        </div>
        {children}
      </div>
    );
  }

  // ── Chart (default) ───────────────────────────────────────────────────────
  return (
    <div
      ref={ref}
      className={`${s.card} ${className || ''}`}
      style={{ ...style, borderTop: `3px solid ${ac}` }}
      data-widget-id={widget.id}
    >
      <style>{KEYFRAMES}</style>
      <div className={s.chartHead}>
        {/* Left: drag + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flex: 1 }}>
          {!readOnly && (
            <span className={`${s.drag} widget-drag-handle`}><DotsSixVertical size={12} weight="regular" /></span>
          )}
          <span className={s.chartTitle}>{widget.title}</span>
        </div>
        {/* Right: compact dark-pill type switcher + controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {/* Dark-pill chart type switcher — 3 options, active pill is filled dark */}
          <div style={{ display: 'flex', gap: 1, background: '#F3F4F6', borderRadius: 6, padding: 3 }}>
            {CHART_SWITCHER.map((opt) => {
              const active = currentChartType === opt.key
                // Also highlight 'bar' when type is 'area' (fallback display)
                || (opt.key === 'bar' && currentChartType === 'area')
                || (opt.key === 'donut' && currentChartType === 'pie');
              return (
                <button
                  key={opt.key}
                  onClick={() => onChangeChartType(widget.id, opt.key)}
                  style={{
                    padding: '3px 9px',
                    borderRadius: 4, border: 'none',
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.015em',
                    background: active ? '#0D1117' : 'transparent',
                    color: active ? '#FFFFFF' : '#9CA3AF',
                    cursor: 'pointer',
                    transition: 'background 0.12s, color 0.12s',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {/* Legend pencil */}
          {onUpdateLegendLabels && (
            <TooltipHost content="Rename series">
              <button
                className={`${s.ctrlBtn} ${s.ctrlWrap}`}
                style={{ opacity: 1, background: editingLegend ? '#D1FAE5' : undefined, color: editingLegend ? '#065F46' : undefined }}
                onClick={() => { if (!editingLegend) setLegendDraft(widget.legendLabels || {}); setEditingLegend((v) => !v); }}
              >
                <PencilSimple size={10} weight="regular" />
              </button>
            </TooltipHost>
          )}
          {!readOnly && <Controls widget={widget} onRemove={onRemove} onResize={onResize} onChangeWidgetType={onChangeWidgetType} />}
        </div>
      </div>
      <div className={s.chartBody}>
        <ErrorBoundary compact label={widget.title}>
          {widget.chartData ? (
            <div style={{ flex: 1, minHeight: 0 }}>
              <ChartPanel
                data={widget.chartData}
                chartType={currentChartType}
                height={widget.size === 'small' ? 200 : widget.size === 'large' ? 400 : 285}
                palette={widget.colorScheme}
                legendLabels={widget.legendLabels}
                embedded
              />
            </div>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 12.5 }}>
              No data loaded
            </div>
          )}
        </ErrorBoundary>
      </div>
      {/* Legend editor */}
      {editingLegend && widget.chartData?.fields && widget.chartData?.rows && onUpdateLegendLabels && (() => {
        const numCols = (widget.chartData!.fields || [])
          .filter((f) => {
            const vals = (widget.chartData!.rows || []).slice(0, 5).map((r) => r[f.name]);
            return vals.some((v) => typeof v === 'number' || (!isNaN(Number(String(v).replace(/[$,]/g, ''))) && String(v).trim() !== ''));
          })
          .map((f) => f.name);
        const pal = widget.colorScheme || ['#2e873d', '#55a655', '#7BC47B'];
        return (
          <div className={s.legendWrap}>
            {numCols.map((col, i) => (
              <div key={col} className={s.legendRow}>
                <div className={s.legendDot} style={{ background: pal[i % pal.length] }} />
                <input
                  className={s.legendInput}
                  value={legendDraft[col] ?? humanizeColumn(col)}
                  onChange={(e) => setLegendDraft((prev) => ({ ...prev, [col]: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { onUpdateLegendLabels(widget.id, legendDraft); setEditingLegend(false); }
                  }}
                />
              </div>
            ))}
            <button className={s.legendSave} onClick={() => { onUpdateLegendLabels(widget.id, legendDraft); setEditingLegend(false); }}>
              Apply
            </button>
          </div>
        );
      })()}
      {children}
    </div>
  );
});

export default DashboardWidgetCard;
